import React, { useState, useEffect, useMemo, useRef } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import axiosInstance from "../../config/axiosConfig";
import Actions from "../../components/courses/Actions";
import { MAX_SIZE_MB } from "../../helper";
import { prefixArray, designationArray } from "../../helper/data";
import CreateForm from "./components/CreateForm";
import EditForm from "./components/EditForm";
import {
  extractDeanOfFacultyId,
  stripDeanMarker,
  withDeanMarker,
} from "../../helper/deanOfFaculty";
import {
  extractEmployeeOrder,
  stripEmployeeMarkers,
  withEmployeeMarkers,
  getDesignationRank,
  sortEmployeesComparator,
  categorizeDepartmentEmployees,
} from "../../helper/employeeOrder";

const normalizeDesignation = (desig) => {
  if (!desig) return "";
  const trimmed = String(desig).trim();
  const matched = designationArray.find(
    (opt) => opt.toLowerCase() === trimmed.toLowerCase()
  );
  return matched || trimmed;
};

const mapFacultyOptions = (items = []) =>
  items.map((item) => ({
    facultyId: item._id,
    name: item.name,
    description: item.description,
  }));

const mapDepartmentOptions = (items = []) =>
  items.map((item) => ({
    deptId: item._id,
    facultyId: item.facultyId,
    name: item.name,
    description: item.description,
  }));

const customModalStyles = {
  content: {
    padding: "20px",
    width: "80%",
    maxWidth: "600px",
    margin: "auto",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
};

const emptyFormData = {
  prefix: "",
  firstName: "",
  lastName: "",
  profileimg: "",
  designation: "",
  interests: "",
  facultyId: "",
  deptId: "",
  phoneNo: "",
  email: "",
  googleScholar: "",
  linkedin: "",
  bio: "",
  resume: "",
  hod: false,
  officeId: "",
  experienceSince: "",
  deanOfFacultyId: "",
  order: "",
};

const buildEmployeeFormData = (formData, imgFile, resumeFile) => {
  const formDataToSend = new FormData();
  const interests = withEmployeeMarkers(
    formData.interests,
    formData.deanOfFacultyId,
    formData.order
  );

  const payload = {
    prefix: formData.prefix || "",
    firstName: formData.firstName || "",
    lastName: formData.lastName || "",
    designation: formData.designation || "",
    interests: interests || " ",
    facultyId: formData.facultyId || "",
    deptId: formData.deptId || "",
    phoneNo: formData.phoneNo || "",
    email: formData.email || "",
    googleScholar: formData.googleScholar || formData.google_scholar || "",
    linkedin: formData.linkedin || "",
    bio: formData.bio || "",
    hod: Boolean(formData.hod),
    officeId: formData.officeId || "",
    experienceSince: formData.experienceSince || "",
    deanOfFacultyId: formData.deanOfFacultyId || "",
    order:
      formData.order !== "" && formData.order !== null && formData.order !== undefined
        ? String(formData.order)
        : "",
    sort_order:
      formData.order !== "" && formData.order !== null && formData.order !== undefined
        ? String(formData.order)
        : "",
  };

  Object.entries(payload).forEach(([key, value]) => {
    formDataToSend.append(key, value);
  });

  if (imgFile) {
    formDataToSend.append("profileimg", imgFile);
  }
  if (resumeFile) {
    formDataToSend.append("resume", resumeFile);
  }

  return formDataToSend;
};

const Employee = () => {
  const [employees, setemployees] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const [formData, setFormData] = useState(emptyFormData);
  const [loading, setLoading] = useState(false);
  const [imgFile, setImgFile] = useState(null);
  const [resumeFile, setresumeFile] = useState(null);
  const [editEmployeeId, setEditEmployeeId] = useState("");
  const [deptArray, setDeptArray] = useState([]);
  const [facultiesArray, setFacultiesArray] = useState([]);
  const [departmentsArray, setDepartmentsArray] = useState([]);
  const [allOffices, setOffices] = useState([]);
  const [sortByLatest, setSortByLatest] = useState(false);
  const [imgPreview, setImgPreview] = useState(null);

  // Sorting & Filtering navigation state
  const [selectedFacultyId, setSelectedFacultyId] = useState("all");
  const [selectedDeptId, setSelectedDeptId] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("department"); // "department" | "flat"

  // Drag and Drop state
  const [draggedItem, setDraggedItem] = useState(null); // { employeeId, index, sectionKey }
  const [dragOverTarget, setDragOverTarget] = useState(null); // { employeeId, index, sectionKey }
  const [savingSectionKey, setSavingSectionKey] = useState(null);
  const dragDataRef = useRef(null);

  const showToast = (message) => {
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: false,
    });
    Toast.fire({
      icon: "success",
      title: message,
    });
  };

  useEffect(() => {
    if (formData.img) {
      setImgPreview(formData.img);
    }
  }, [formData.img]);

  useEffect(() => {
    fetchemployees();
    fetchFacultyOptions();
  }, []);

  const fetchFacultyOptions = async () => {
    try {
      const [facultyRes, departmentRes] = await Promise.all([
        axiosInstance.get("faculty/all"),
        axiosInstance.get("department/all"),
      ]);
      setFacultiesArray(mapFacultyOptions(facultyRes?.data?.data || []));
      setDepartmentsArray(mapDepartmentOptions(departmentRes?.data?.data || []));
    } catch (error) {
      console.error("Error fetching faculties/departments:", error);
    }
  };

  const fetchemployees = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("employee/all");
      setemployees(response?.data?.data || []);
      const res = await axiosInstance.get("office/all");
      setOffices(res?.data?.data || []);
    } catch (error) {
      console.error("Error fetching employees:", error);
      Swal.fire({
        icon: "error",
        title: "Error fetching employees!",
        text: "Please try again.",
        timer: 1500,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formDataToSend = buildEmployeeFormData(formData, imgFile, resumeFile);

    try {
      await axiosInstance.post("employee/create", formDataToSend);
      await fetchemployees();
      Swal.fire({
        icon: "success",
        title: "Employee created successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
      setShowCreateModal(false);
      setFormData(emptyFormData);
      setImgFile(null);
      setresumeFile(null);
      setImgPreview(null);
    } catch (error) {
      console.error("Error creating employee:", error);
      Swal.fire({
        icon: "error",
        title: "Error creating employee!",
        text: error.response?.data?.message || "Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEmployee = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formDataToSend = buildEmployeeFormData(formData, imgFile, resumeFile);
    formDataToSend.append("_method", "PUT");
    try {
      await axiosInstance.post(
        `employee/change/${editEmployeeId}`,
        formDataToSend
      );
      await fetchemployees();
      setShowEditModal(false);
      setFormData(emptyFormData);
      setImgFile(null);
      setresumeFile(null);
      setImgPreview(null);
      setDeptArray([]);
      Swal.fire({
        icon: "success",
        title: "Employee updated successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error("Error updating employee:", error);
      const apiErrors = error.response?.data?.errors;
      const message = apiErrors
        ? Object.values(apiErrors).flat().join(" ")
        : error.response?.data?.message ||
          (error.code === "ERR_NETWORK"
            ? "Cannot reach API. Is uo-api running on port 8001?"
            : "Please try again.");
      Swal.fire({
        icon: "error",
        title: "Error updating employee!",
        text: message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewEmployee = async (id) => {
    try {
      setShowEditModal(true);
      setEditEmployeeId(id);
      setImgFile(null);
      setresumeFile(null);
      const response = await axiosInstance.get(`employee/single/${id}`);
      const data = response?.data?.data || {};
      const deanOfFacultyId = extractDeanOfFacultyId(
        data.interests,
        data.deanOfFacultyId
      );
      const existingOrder = extractEmployeeOrder(data);

      setFormData({
        ...emptyFormData,
        prefix: data.prefix || "",
        firstName: data.firstName || "",
        lastName: data.lastName || "",
        profileimg: data.profileimg || "",
        facultyId: data.facultyId ? String(data.facultyId) : "",
        deptId: data.deptId ? String(data.deptId) : "",
        designation: normalizeDesignation(data.designation || ""),
        interests: stripEmployeeMarkers(data.interests || ""),
        phoneNo: data.phoneNo || "",
        email: data.email || "",
        googleScholar: data.googleScholar || data.google_scholar || "",
        linkedin: data.linkedin || "",
        hod: Boolean(data.hod),
        resume: data.resume || "",
        bio: data.bio || "",
        experienceSince: data.experienceSince || "",
        officeId: data.office?._id || data.officeId || "",
        deanOfFacultyId,
        order: existingOrder !== null ? existingOrder : "",
      });
      setImgPreview(data.profileimg || null);
      if (data?.facultyId) {
        const matchingDepartments = departmentsArray.filter(
          (dept) => String(dept.facultyId) === String(data.facultyId)
        );
        setDeptArray(matchingDepartments);
      } else {
        setDeptArray([]);
      }
      const res = await axiosInstance.get("office/all");
      setOffices(res?.data?.data || []);
    } catch (error) {
      console.error("Error fetching employee:", error);
      Swal.fire({
        icon: "error",
        title: "Error fetching employee!",
        text: "Please try again.",
        timer: 1500,
      });
    }
  };

  const handleDeleteEmployee = async (employeeId) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        await axiosInstance.delete(`employee/delete/${employeeId}`);
        Swal.fire({
          icon: "success",
          title: "Employee deleted successfully!",
          showConfirmButton: false,
          timer: 1500,
        });
        fetchemployees();
      }
    } catch (error) {
      console.error("Error deleting employee:", error);
      Swal.fire({
        icon: "error",
        title: "Error deleting employee!",
        text: "Please try again.",
        timer: 1500,
      });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (name === "facultyId") {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
        deptId: "",
      }));
      const matchingDepartments = departmentsArray.filter(
        (dept) => String(dept.facultyId) === String(value)
      );
      setDeptArray(matchingDepartments || []);
    }
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];

    if (file) {
      const fileSizeInMB = file.size / (1024 * 1024);
      if (fileSizeInMB > MAX_SIZE_MB) {
        alert(
          `File size exceeds ${MAX_SIZE_MB} MB. Please upload a smaller file.`
        );
        e.target.value = "";
        return;
      }

      if (name === "profileimg") {
        setImgFile(file);
        setImgPreview(URL.createObjectURL(file));
      } else if (name === "resume") {
        setresumeFile(file);
      }
    }
  };

  const fields = [
    { label: "Prefix", name: "prefix", type: "dropdown", options: prefixArray },
    { label: "First Name", name: "firstName", type: "text", required: true },
    { label: "Last Name", name: "lastName", type: "text", required: true },
    {
      label: "Designation",
      name: "designation",
      type: "dropdown",
      options: designationArray,
    },
    {
      label: "Display Order (in designation)",
      name: "order",
      type: "number",
    },
    { label: "interests", name: "interests", type: "text" },
    {
      label: "faculty",
      name: "facultyId",
      type: "dropdown",
      options: facultiesArray,
    },
    { label: "Department", name: "deptId", type: "dropdown", options: deptArray },
    {
      label: "Dean of Faculty",
      name: "deanOfFacultyId",
      type: "dropdown",
      options: facultiesArray,
    },
    { label: "phoneNo", name: "phoneNo", type: "text", required: true },
    { label: "email", name: "email", type: "text", required: true },
    { label: "Google Scholar Profile URL", name: "googleScholar", type: "text" },
    { label: "LinkedIn Profile URL", name: "linkedin", type: "text" },
    { label: "Bio", name: "bio", type: "textarea" },
    { label: "Experience Since", name: "experienceSince", type: "date" },
    {
      label: "Office",
      name: "officeId",
      type: "dropdown",
      options: allOffices,
    },
  ];

  // Helper to save a single employee with updated order & interests marker
  const saveEmployeeOrder = async (employee, newOrder) => {
    const cleanInterests = stripEmployeeMarkers(employee.interests);
    const deanId = extractDeanOfFacultyId(employee.interests, employee.deanOfFacultyId);
    const updatedInterests = withEmployeeMarkers(cleanInterests, deanId, newOrder);

    const formDataToSend = new FormData();
    formDataToSend.append("_method", "PUT");
    formDataToSend.append("firstName", employee.firstName || "");
    formDataToSend.append("lastName", employee.lastName || "");
    formDataToSend.append("designation", employee.designation || "");
    formDataToSend.append("phoneNo", employee.phoneNo || "");
    formDataToSend.append("email", employee.email || "");
    formDataToSend.append("facultyId", employee.facultyId || "");
    formDataToSend.append("deptId", employee.deptId || "");
    formDataToSend.append("interests", updatedInterests || " ");
    formDataToSend.append("order", String(newOrder));
    formDataToSend.append("sort_order", String(newOrder));
    if (employee.experienceSince) formDataToSend.append("experienceSince", employee.experienceSince);
    if (employee.officeId) formDataToSend.append("officeId", employee.officeId);
    formDataToSend.append("hod", employee.hod ? "1" : "0");
    if (deanId) formDataToSend.append("deanOfFacultyId", deanId);

    return axiosInstance.post(`employee/change/${employee._id}`, formDataToSend);
  };

  // Save the entire reordered section to the backend
  const saveReorderedSection = async (sectionEmployees, sectionName, sectionKey) => {
    setSavingSectionKey(sectionKey);
    // 1. Try modern batch reorder API first
    try {
      const items = sectionEmployees.map((emp, idx) => ({
        id: emp._id,
        _id: emp._id,
        order: idx + 1,
        sort_order: idx + 1,
      }));
      await axiosInstance.post("employee/reorder", { items });
      showToast(`${sectionName || "Section"} order saved!`);
      setSavingSectionKey(null);
      return;
    } catch (batchErr) {
      console.log("Batch reorder not yet deployed on server, using individual employee updates:", batchErr);
    }

    // 2. Fallback: Save per employee with updated interests marker & order field
    try {
      await Promise.all(
        sectionEmployees.map((emp, idx) => saveEmployeeOrder(emp, idx + 1))
      );
      showToast(`${sectionName || "Section"} order saved!`);
    } catch (err) {
      console.error("Error saving employee order:", err);
      Swal.fire({
        icon: "error",
        title: "Failed to save order",
        text: "Please try again.",
        timer: 1500,
      });
    } finally {
      setSavingSectionKey(null);
    }
  };

  // Reorder items in a section and update state & server
  const moveEmployee = (sectionKey, fromIndex, toIndex, sectionEmployees, sectionTitle) => {
    if (fromIndex === toIndex || toIndex < 0 || toIndex >= sectionEmployees.length) return;

    const updated = [...sectionEmployees];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);

    const idToNewOrder = {};
    const idToNewInterests = {};
    updated.forEach((emp, i) => {
      const newOrder = i + 1;
      idToNewOrder[emp._id] = newOrder;
      const clean = stripEmployeeMarkers(emp.interests);
      const deanId = extractDeanOfFacultyId(emp.interests, emp.deanOfFacultyId);
      idToNewInterests[emp._id] = withEmployeeMarkers(clean, deanId, newOrder);
    });

    // Immediate optimistic local state update
    setemployees((prev) =>
      prev.map((emp) => {
        if (idToNewOrder[emp._id] !== undefined) {
          return {
            ...emp,
            order: idToNewOrder[emp._id],
            sort_order: idToNewOrder[emp._id],
            interests: idToNewInterests[emp._id],
          };
        }
        return emp;
      })
    );

    const employeesToSave = updated.map((emp, i) => ({
      ...emp,
      order: i + 1,
      sort_order: i + 1,
      interests: idToNewInterests[emp._id],
    }));

    saveReorderedSection(employeesToSave, sectionTitle, sectionKey);
  };

  // Drag and Drop Handlers
  const handleDragStart = (e, employee, index, sectionKey) => {
    dragDataRef.current = { employeeId: employee._id, index, sectionKey };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", employee._id);
    setDraggedItem({ employeeId: employee._id, index, sectionKey });
  };

  const handleDragOver = (e, employee, index, sectionKey) => {
    // If not dragging from the EXACT same section, do not allow drop
    if (!dragDataRef.current || dragDataRef.current.sectionKey !== sectionKey) {
      e.dataTransfer.dropEffect = "none";
      return;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverTarget?.employeeId !== employee._id) {
      setDragOverTarget({ employeeId: employee._id, index, sectionKey });
    }
  };

  const handleDrop = (e, targetEmployee, targetIndex, sectionKey, sectionEmployees, sectionTitle) => {
    e.preventDefault();
    if (!dragDataRef.current || dragDataRef.current.sectionKey !== sectionKey) {
      setDraggedItem(null);
      setDragOverTarget(null);
      dragDataRef.current = null;
      return;
    }

    const fromIndex = dragDataRef.current.index;
    setDraggedItem(null);
    setDragOverTarget(null);
    dragDataRef.current = null;

    if (fromIndex !== targetIndex) {
      moveEmployee(sectionKey, fromIndex, targetIndex, sectionEmployees, sectionTitle);
    }
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverTarget(null);
    dragDataRef.current = null;
  };

  // Filtered employees by search term
  const searchFilteredEmployees = useMemo(() => {
    if (!searchTerm.trim()) return employees;
    const term = searchTerm.toLowerCase().trim();
    return employees.filter((emp) => {
      const name = `${emp.firstName || ""} ${emp.lastName || ""}`.toLowerCase();
      const desig = (emp.designation || "").toLowerCase();
      const email = (emp.email || "").toLowerCase();
      return name.includes(term) || desig.includes(term) || email.includes(term);
    });
  }, [employees, searchTerm]);

  // Grouped structure: Faculties -> Departments -> Sections
  const facultyGroupedData = useMemo(() => {
    const list = searchFilteredEmployees;

    // Map departments by id
    const deptMap = {};
    departmentsArray.forEach((d) => {
      deptMap[String(d.deptId)] = d;
    });

    // 1. Group by Faculty
    const facultiesWithDepts = facultiesArray.map((faculty) => {
      const facId = String(faculty.facultyId);
      const facDepts = departmentsArray.filter(
        (dept) => String(dept.facultyId) === facId
      );

      const deptsData = facDepts.map((dept) => {
        const deptId = String(dept.deptId);
        const deptEmployees = list.filter(
          (emp) => String(emp.deptId) === deptId
        );
        const categories = categorizeDepartmentEmployees(deptEmployees);

        return {
          dept,
          deptId,
          totalCount: deptEmployees.length,
          categories,
        };
      });

      // Employees assigned to faculty but without department
      const unassignedDeptEmployees = list.filter(
        (emp) => String(emp.facultyId) === facId && (!emp.deptId || !deptMap[String(emp.deptId)])
      );

      const totalFacultyEmployees = list.filter(
        (emp) => String(emp.facultyId) === facId
      ).length;

      return {
        faculty,
        facultyId: facId,
        departments: deptsData,
        facultyLevelEmployees: categorizeDepartmentEmployees(unassignedDeptEmployees),
        facultyLevelCount: unassignedDeptEmployees.length,
        totalCount: totalFacultyEmployees,
      };
    });

    // 2. Office employees & Unassigned
    const officeEmployees = list.filter(
      (emp) => emp.officeId && !emp.facultyId && !emp.deptId
    );
    const orphanEmployees = list.filter(
      (emp) => !emp.facultyId && !emp.deptId && !emp.officeId
    );

    return {
      faculties: facultiesWithDepts,
      officeEmployees: categorizeDepartmentEmployees(officeEmployees),
      officeCount: officeEmployees.length,
      orphanEmployees: categorizeDepartmentEmployees(orphanEmployees),
      orphanCount: orphanEmployees.length,
    };
  }, [searchFilteredEmployees, facultiesArray, departmentsArray]);

  // Flat sorted employees for flat view mode
  const flatSortedEmployees = useMemo(() => {
    if (sortByLatest) {
      return [...searchFilteredEmployees].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
    }
    return [...searchFilteredEmployees].sort(sortEmployeesComparator);
  }, [searchFilteredEmployees, sortByLatest]);

  // Visible faculties based on selected faculty filter
  const visibleFaculties = useMemo(() => {
    if (selectedFacultyId === "all") {
      return facultyGroupedData.faculties;
    }
    return facultyGroupedData.faculties.filter(
      (f) => String(f.facultyId) === String(selectedFacultyId)
    );
  }, [facultyGroupedData.faculties, selectedFacultyId]);

  // Draggable Card component
  const renderEmployeeCard = (employee, index, sectionEmployees, sectionTitle, sectionKey) => {
    const isDragging = draggedItem?.employeeId === employee._id;
    const isDragOver = dragOverTarget?.employeeId === employee._id && dragOverTarget?.sectionKey === sectionKey;
    const canDrag = sectionEmployees.length > 1;
    const currentOrder = extractEmployeeOrder(employee);
    const hasExplicitOrder = currentOrder !== null;

    return (
      <div
        key={employee._id}
        draggable={canDrag}
        onDragStart={(e) => canDrag && handleDragStart(e, employee, index, sectionKey)}
        onDragOver={(e) => canDrag && handleDragOver(e, employee, index, sectionKey)}
        onDrop={(e) => canDrag && handleDrop(e, employee, index, sectionKey, sectionEmployees, sectionTitle)}
        onDragEnd={handleDragEnd}
        style={{
          width: "250px",
          minWidth: "250px",
          flexShrink: 0,
          padding: "14px",
          borderRadius: "10px",
          backgroundColor: isDragOver ? "#eff6ff" : "#fff",
          border: isDragOver
            ? "2px dashed #2563eb"
            : isDragging
            ? "2px solid #94a3b8"
            : "1px solid #e2e8f0",
          boxShadow: isDragOver
            ? "0 8px 16px rgba(37, 99, 235, 0.18)"
            : "0 2px 6px rgba(0, 0, 0, 0.05)",
          opacity: isDragging ? 0.35 : 1,
          transform: isDragOver ? "scale(1.02)" : "scale(1)",
          transition: "transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          cursor: canDrag ? "grab" : "default",
        }}
      >
        <div>
          {/* Header row with move buttons, drag indicator, and order badge */}
          {canDrag && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "10px",
                paddingBottom: "8px",
                borderBottom: "1px dashed #e2e8f0",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <button
                  type="button"
                  disabled={index === 0}
                  onClick={(e) => {
                    e.stopPropagation();
                    moveEmployee(sectionKey, index, index - 1, sectionEmployees, sectionTitle);
                  }}
                  title="Move Left (Earlier in display order)"
                  style={{
                    cursor: index === 0 ? "not-allowed" : "pointer",
                    opacity: index === 0 ? 0.3 : 1,
                    border: "1px solid #cbd5e1",
                    background: "#f8fafc",
                    borderRadius: "4px",
                    padding: "2px 7px",
                    fontSize: "11px",
                    fontWeight: "bold",
                    color: "#334155",
                  }}
                >
                  ◀
                </button>
                <button
                  type="button"
                  disabled={index === sectionEmployees.length - 1}
                  onClick={(e) => {
                    e.stopPropagation();
                    moveEmployee(sectionKey, index, index + 1, sectionEmployees, sectionTitle);
                  }}
                  title="Move Right (Later in display order)"
                  style={{
                    cursor: index === sectionEmployees.length - 1 ? "not-allowed" : "pointer",
                    opacity: index === sectionEmployees.length - 1 ? 0.3 : 1,
                    border: "1px solid #cbd5e1",
                    background: "#f8fafc",
                    borderRadius: "4px",
                    padding: "2px 7px",
                    fontSize: "11px",
                    fontWeight: "bold",
                    color: "#334155",
                  }}
                >
                  ▶
                </button>
              </div>

              <span
                style={{
                  fontSize: "11px",
                  color: "#64748b",
                  display: "flex",
                  alignItems: "center",
                  gap: "2px",
                  userSelect: "none",
                  fontWeight: 500,
                }}
              >
                <span style={{ fontSize: "14px", lineHeight: "1" }}>⠿</span> Drag
              </span>

              <span
                style={{
                  backgroundColor: hasExplicitOrder ? "#2563eb" : "#64748b",
                  color: "#fff",
                  fontSize: "10px",
                  fontWeight: 700,
                  padding: "2px 6px",
                  borderRadius: "10px",
                }}
                title={hasExplicitOrder ? `Custom Order #${currentOrder}` : `Seniority Position #${index + 1}`}
              >
                #{index + 1}
              </span>
            </div>
          )}

          <div style={{ textAlign: "center" }}>
            <img
              src={
                employee.profileimg ||
                `https://placehold.co/128x128?text=${(employee.firstName?.[0] || "U")}${(employee.lastName?.[0] || "O")}`
              }
              alt={employee.firstName}
              style={{
                width: 110,
                height: 110,
                borderRadius: "50%",
                objectFit: "cover",
                margin: "0 auto 10px",
                border: "2px solid #e2e8f0",
              }}
            />
            <h3
              style={{
                margin: "0 0 4px",
                fontSize: "15px",
                fontWeight: 700,
                color: "#1e293b",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={`${employee.firstName || ""} ${employee.lastName || ""}`}
            >
              {employee.prefix ? `${employee.prefix} ` : ""}
              {employee.firstName} {employee.lastName}
            </h3>
            <p
              style={{
                margin: "0 0 6px",
                fontSize: "13px",
                color: "#475569",
                fontWeight: 500,
              }}
            >
              {employee.designation || "No Designation"}
            </p>

            {/* Badges */}
            <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "4px", margin: "4px 0 8px" }}>
              {employee.hod && (
                <span
                  style={{
                    backgroundColor: "#dbeafe",
                    color: "#1e40af",
                    fontSize: "11px",
                    fontWeight: 700,
                    padding: "2px 7px",
                    borderRadius: "4px",
                  }}
                >
                  HOD
                </span>
              )}
              {extractDeanOfFacultyId(employee.interests, employee.deanOfFacultyId) && (
                <span
                  style={{
                    backgroundColor: "#fef3c7",
                    color: "#92400e",
                    fontSize: "11px",
                    fontWeight: 700,
                    padding: "2px 7px",
                    borderRadius: "4px",
                  }}
                >
                  Dean of Faculty
                </span>
              )}
            </div>

            {/* Social / Scholar links */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "8px",
                margin: "6px 0",
              }}
            >
              {(employee.googleScholar || employee.google_scholar) && (
                <a
                  href={employee.googleScholar || employee.google_scholar}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    fontSize: "11px",
                    color: "#1976d2",
                    textDecoration: "underline",
                    fontWeight: 500,
                  }}
                >
                  🎓 Scholar
                </a>
              )}
              {employee.linkedin && (
                <a
                  href={employee.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    fontSize: "11px",
                    color: "#0077b5",
                    textDecoration: "underline",
                    fontWeight: 500,
                  }}
                >
                  💼 LinkedIn
                </a>
              )}
            </div>
          </div>
        </div>

        <div style={{ marginTop: "10px", paddingTop: "8px", borderTop: "1px solid #f1f5f9" }}>
          <Actions
            courseId={employee._id}
            onEdit={handleViewEmployee}
            onDelete={handleDeleteEmployee}
          />
        </div>
      </div>
    );
  };

  // Render a specific designation category section inside a department
  const renderDesignationSection = (title, employeesList, sectionKey, color = "#2563eb") => {
    if (!employeesList || employeesList.length === 0) return null;
    const isSaving = savingSectionKey === sectionKey;

    return (
      <div
        key={sectionKey}
        style={{
          marginBottom: "24px",
          backgroundColor: "#f8fafc",
          borderRadius: "10px",
          padding: "16px",
          border: "1px solid #e2e8f0",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "12px",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span
              style={{
                display: "inline-block",
                width: "4px",
                height: "18px",
                backgroundColor: color,
                borderRadius: "2px",
              }}
            />
            <h4
              style={{
                margin: 0,
                fontSize: "16px",
                fontWeight: 700,
                color: "#1e293b",
              }}
            >
              {title}{" "}
              <span style={{ fontSize: "13px", fontWeight: 500, color: "#64748b" }}>
                ({employeesList.length})
              </span>
            </h4>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {isSaving && (
              <span style={{ fontSize: "12px", color: "#2563eb", fontWeight: 600 }}>
                Saving order...
              </span>
            )}
            {employeesList.length > 1 ? (
              <span
                style={{
                  fontSize: "12px",
                  color: "#059669",
                  backgroundColor: "#ecfdf5",
                  padding: "3px 8px",
                  borderRadius: "6px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                ↔ Drag left/right to sort
              </span>
            ) : (
              <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                Single member
              </span>
            )}
          </div>
        </div>

        {/* Horizontal row of cards for left/right drag reordering */}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "16px",
            overflowX: "auto",
            padding: "8px 4px 14px",
            alignItems: "stretch",
          }}
        >
          {employeesList.map((employee, idx) =>
            renderEmployeeCard(employee, idx, employeesList, title, sectionKey)
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: "10px", maxWidth: "1400px", margin: "0 auto" }}>
      {/* Top Banner & Main Controls */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "15px",
          marginBottom: "20px",
          backgroundColor: "#fff",
          padding: "20px",
          borderRadius: "10px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
        }}
      >
        <div>
          <h2 style={{ margin: "0 0 6px", fontSize: "22px", fontWeight: 800, color: "#0f172a" }}>
            Employee Management & Hierarchy
          </h2>
          <p style={{ margin: 0, fontSize: "13px", color: "#64748b" }}>
            Organized by Department & Designation. Drag cards left/right within each designation to customize who comes first on the website.
          </p>
        </div>

        <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
          <button
            onClick={() => {
              setFormData(emptyFormData);
              setImgFile(null);
              setresumeFile(null);
              setImgPreview(null);
              setDeptArray([]);
              setEditEmployeeId("");
              setShowCreateModal(true);
            }}
            style={{
              padding: "10px 18px",
              backgroundColor: "#16a34a",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              boxShadow: "0 2px 4px rgba(22, 163, 74, 0.2)",
            }}
          >
            <span>+</span> Create New Employee
          </button>

          <button
            onClick={() => setViewMode((prev) => (prev === "department" ? "flat" : "department"))}
            style={{
              padding: "10px 16px",
              backgroundColor: viewMode === "department" ? "#2563eb" : "#f1f5f9",
              color: viewMode === "department" ? "#fff" : "#334155",
              border: "1px solid #cbd5e1",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "13px",
            }}
          >
            {viewMode === "department" ? "🏛 Department View" : "📋 Flat Grid View"}
          </button>
        </div>
      </div>

      {/* Search & Faculty Navigation Bar */}
      <div
        style={{
          backgroundColor: "#fff",
          padding: "16px 20px",
          borderRadius: "10px",
          marginBottom: "20px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
        }}
      >
        {/* Search input */}
        <div style={{ marginBottom: "16px" }}>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="🔍 Search employees by name, email, or designation..."
            style={{
              width: "100%",
              padding: "10px 14px",
              borderRadius: "6px",
              border: "1px solid #cbd5e1",
              fontSize: "14px",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>

        {/* Faculty Tabs */}
        <div style={{ display: "flex", gap: "8px", overflowX: "auto", paddingBottom: "6px" }}>
          <button
            onClick={() => {
              setSelectedFacultyId("all");
              setSelectedDeptId("all");
            }}
            style={{
              padding: "8px 14px",
              borderRadius: "6px",
              border: "none",
              backgroundColor: selectedFacultyId === "all" ? "#1e293b" : "#f1f5f9",
              color: selectedFacultyId === "all" ? "#fff" : "#475569",
              fontWeight: 600,
              fontSize: "13px",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            All Faculties ({employees.length})
          </button>

          {facultyGroupedData.faculties.map((f) => (
            <button
              key={f.facultyId}
              onClick={() => {
                setSelectedFacultyId(f.facultyId);
                setSelectedDeptId("all");
              }}
              style={{
                padding: "8px 14px",
                borderRadius: "6px",
                border: "none",
                backgroundColor: String(selectedFacultyId) === String(f.facultyId) ? "#2563eb" : "#f1f5f9",
                color: String(selectedFacultyId) === String(f.facultyId) ? "#fff" : "#475569",
                fontWeight: 600,
                fontSize: "13px",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {f.faculty.name} ({f.totalCount})
            </button>
          ))}

          <button
            onClick={() => {
              setSelectedFacultyId("offices");
              setSelectedDeptId("all");
            }}
            style={{
              padding: "8px 14px",
              borderRadius: "6px",
              border: "none",
              backgroundColor: selectedFacultyId === "offices" ? "#0f766e" : "#f1f5f9",
              color: selectedFacultyId === "offices" ? "#fff" : "#475569",
              fontWeight: 600,
              fontSize: "13px",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Offices & Administration ({facultyGroupedData.officeCount})
          </button>
        </div>

        {/* Department sub-filter if a specific faculty is selected */}
        {selectedFacultyId !== "all" && selectedFacultyId !== "offices" && (
          <div style={{ marginTop: "12px", display: "flex", alignItems: "center", gap: "10px" }}>
            <label style={{ fontSize: "13px", fontWeight: 600, color: "#64748b" }}>
              Filter Department:
            </label>
            <select
              value={selectedDeptId}
              onChange={(e) => setSelectedDeptId(e.target.value)}
              style={{
                padding: "6px 12px",
                borderRadius: "6px",
                border: "1px solid #cbd5e1",
                fontSize: "13px",
              }}
            >
              <option value="all">All Departments in this Faculty</option>
              {departmentsArray
                .filter((d) => String(d.facultyId) === String(selectedFacultyId))
                .map((d) => (
                  <option key={d.deptId} value={String(d.deptId)}>
                    {d.name}
                  </option>
                ))}
            </select>
          </div>
        )}
      </div>

      {/* Main Content Area */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <p style={{ fontSize: "16px", color: "#64748b", fontWeight: 600 }}>
            Loading employees and academic hierarchy...
          </p>
        </div>
      ) : viewMode === "flat" ? (
        /* Flat Grid View Mode */
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "15px" }}>
            <button
              onClick={() => setSortByLatest((prev) => !prev)}
              style={{
                padding: "8px 16px",
                backgroundColor: sortByLatest ? "#2563eb" : "#f1f5f9",
                color: sortByLatest ? "#fff" : "#334155",
                border: "1px solid #cbd5e1",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "13px",
              }}
            >
              {sortByLatest ? "Sorted by: Latest Created" : "Sorted by: Designation & Order"}
            </button>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
            {flatSortedEmployees.map((employee, idx) =>
              renderEmployeeCard(employee, idx, flatSortedEmployees, "All Employees", "flat_all")
            )}
          </div>
        </div>
      ) : selectedFacultyId === "offices" ? (
        /* Offices & Administration View */
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "10px",
            padding: "20px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
          }}
        >
          <h3 style={{ margin: "0 0 16px", fontSize: "18px", color: "#0f766e" }}>
            🏛 Offices & Administrative Staff
          </h3>
          {renderDesignationSection(
            "Professors & Senior Officials",
            facultyGroupedData.officeEmployees.professors,
            "office_professors",
            "#0f766e"
          )}
          {renderDesignationSection(
            "Associate Professors / Officers",
            facultyGroupedData.officeEmployees.associateProfessors,
            "office_associateProfessors",
            "#0d9488"
          )}
          {renderDesignationSection(
            "Assistant Professors / Staff",
            facultyGroupedData.officeEmployees.assistantProfessors,
            "office_assistantProfessors",
            "#14b8a6"
          )}
          {renderDesignationSection(
            "Lecturers",
            facultyGroupedData.officeEmployees.lecturers,
            "office_lecturers",
            "#059669"
          )}
          {renderDesignationSection(
            "Administrative & Other Staff",
            facultyGroupedData.officeEmployees.otherMembers,
            "office_otherMembers",
            "#64748b"
          )}
        </div>
      ) : (
        /* Hierarchy View: Grouped by Faculty and Department */
        <div style={{ display: "flex", flexDirection: "column", gap: "30px" }}>
          {visibleFaculties.map((facGroup) => {
            const deptsToDisplay =
              selectedDeptId === "all"
                ? facGroup.departments
                : facGroup.departments.filter(
                    (d) => String(d.deptId) === String(selectedDeptId)
                  );

            return (
              <div
                key={facGroup.facultyId}
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "12px",
                  padding: "24px",
                  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                  border: "1px solid #e2e8f0",
                }}
              >
                {/* Faculty Header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingBottom: "16px",
                    borderBottom: "2px solid #e2e8f0",
                    marginBottom: "20px",
                    flexWrap: "wrap",
                    gap: "10px",
                  }}
                >
                  <div>
                    <h3
                      style={{
                        margin: 0,
                        fontSize: "20px",
                        fontWeight: 800,
                        color: "#1e293b",
                      }}
                    >
                      {facGroup.faculty.name}
                    </h3>
                    <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#64748b" }}>
                      {facGroup.faculty.description || "Faculty Overview"} &bull; Total{" "}
                      {facGroup.totalCount} Employees
                    </p>
                  </div>
                </div>

                {/* Departments in this Faculty */}
                {deptsToDisplay.length === 0 ? (
                  <p style={{ color: "#94a3b8", fontStyle: "italic" }}>
                    No departments matching filter.
                  </p>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                    {deptsToDisplay.map((deptGroup) => {
                      const { dept, deptId, categories, totalCount } = deptGroup;

                      return (
                        <div
                          key={deptId}
                          style={{
                            border: "1px solid #cbd5e1",
                            borderRadius: "10px",
                            padding: "20px",
                            backgroundColor: "#ffffff",
                          }}
                        >
                          {/* Department Title */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              marginBottom: "16px",
                              flexWrap: "wrap",
                              gap: "8px",
                            }}
                          >
                            <h4
                              style={{
                                margin: 0,
                                fontSize: "17px",
                                fontWeight: 700,
                                color: "#0f172a",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <span>🏛</span> {dept.name}
                            </h4>
                            <span
                              style={{
                                fontSize: "12px",
                                fontWeight: 600,
                                backgroundColor: "#e2e8f0",
                                color: "#334155",
                                padding: "3px 10px",
                                borderRadius: "12px",
                              }}
                            >
                              {totalCount} {totalCount === 1 ? "Employee" : "Employees"}
                            </span>
                          </div>

                          {/* Designation Sections - Professors, Associate Professors, Assistant Professors, Lecturers, Others */}
                          {totalCount === 0 ? (
                            <p style={{ margin: "10px 0", color: "#94a3b8", fontSize: "13px", fontStyle: "italic" }}>
                              No employees assigned to this department yet.
                            </p>
                          ) : (
                            <div>
                              {renderDesignationSection(
                                "Professors",
                                categories.professors,
                                `${deptId}_professors`,
                                "#1e40af"
                              )}
                              {renderDesignationSection(
                                "Associate Professors",
                                categories.associateProfessors,
                                `${deptId}_associateProfessors`,
                                "#2563eb"
                              )}
                              {renderDesignationSection(
                                "Assistant Professors",
                                categories.assistantProfessors,
                                `${deptId}_assistantProfessors`,
                                "#3b82f6"
                              )}
                              {renderDesignationSection(
                                "Lecturers",
                                categories.lecturers,
                                `${deptId}_lecturers`,
                                "#0284c7"
                              )}
                              {renderDesignationSection(
                                "Other Faculty Members",
                                categories.otherMembers,
                                `${deptId}_otherMembers`,
                                "#64748b"
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Faculty-level Employees without Department */}
                {facGroup.facultyLevelCount > 0 && (
                  <div
                    style={{
                      marginTop: "20px",
                      border: "1px dashed #cbd5e1",
                      borderRadius: "10px",
                      padding: "16px",
                    }}
                  >
                    <h4 style={{ margin: "0 0 12px", fontSize: "15px", color: "#475569" }}>
                      Other Faculty-Level Members ({facGroup.facultyLevelCount})
                    </h4>
                    {renderDesignationSection(
                      "Professors",
                      facGroup.facultyLevelEmployees.professors,
                      `${facGroup.facultyId}_fac_professors`,
                      "#1e40af"
                    )}
                    {renderDesignationSection(
                      "Associate Professors",
                      facGroup.facultyLevelEmployees.associateProfessors,
                      `${facGroup.facultyId}_fac_associateProfessors`,
                      "#2563eb"
                    )}
                    {renderDesignationSection(
                      "Assistant Professors",
                      facGroup.facultyLevelEmployees.assistantProfessors,
                      `${facGroup.facultyId}_fac_assistantProfessors`,
                      "#3b82f6"
                    )}
                    {renderDesignationSection(
                      "Lecturers",
                      facGroup.facultyLevelEmployees.lecturers,
                      `${facGroup.facultyId}_fac_lecturers`,
                      "#0284c7"
                    )}
                    {renderDesignationSection(
                      "Other Members",
                      facGroup.facultyLevelEmployees.otherMembers,
                      `${facGroup.facultyId}_fac_otherMembers`,
                      "#64748b"
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create Employee Modal */}
      <Modal
        isOpen={showCreateModal}
        onRequestClose={() => {
          setFormData(emptyFormData);
          setImgFile(null);
          setresumeFile(null);
          setImgPreview(null);
          setDeptArray([]);
          setShowCreateModal(false);
        }}
        style={customModalStyles}
        contentLabel="Create New Employee"
      >
        <h2>Create New Employee</h2>
        <CreateForm
          formData={formData}
          handleChange={handleChange}
          handleFileChange={handleFileChange}
          loading={loading}
          handleCreate={handleCreateEmployee}
          fields={fields}
          imgPreview={imgPreview}
        />
      </Modal>

      {/* Edit Employee Modal */}
      <Modal
        isOpen={showEditModal}
        onRequestClose={() => {
          setFormData(emptyFormData);
          setImgPreview(null);
          setImgFile(null);
          setresumeFile(null);
          setShowEditModal(false);
        }}
        style={customModalStyles}
        contentLabel="View Employee"
      >
        <h2>Update Employee</h2>
        <EditForm
          formData={formData}
          handleChange={handleChange}
          handleFileChange={handleFileChange}
          loading={loading}
          handleEdit={handleChangeEmployee}
          fields={fields}
          imgPreview={imgPreview}
        />
      </Modal>
    </div>
  );
};

export default Employee;
